import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { MvrPreview, previewMvr } from '../../services/mvrFetchApi';

// Motor Vehicle Record preview, as the Toolbox shows it:
//   Activity History  -> Personal Information / Violations / Accidents / License
//   Transactional MVR -> the PDF itself

interface Props {
  quoteId: string;
  transactionId: string;
  driverName?: string;
  onClose: () => void;
}

const val = (v: any) => (v === null || v === undefined || v === '' ? 'N/A' : String(v));

const MvrPreviewModal: React.FC<Props> = ({ quoteId, transactionId, driverName, onClose }) => {
  const [preview, setPreview] = useState<MvrPreview | null>(null);
  // Printed from a clone in an isolated frame, so only the record reaches the page.
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: 'Motor Vehicle Record' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    previewMvr(quoteId, transactionId)
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the MVR.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [quoteId, transactionId]);

  const record = preview?.record;
  const person = record?.person ?? {};
  const address = record?.address ?? {};
  const addressLine = [address.street, address.city, address.state, address.zipCode]
    .filter(Boolean).join(', ');

  return (
    <div className="mvrp-overlay" onClick={onClose}>
      <div className="mvrp-panel" onClick={(e) => e.stopPropagation()}>
        <div className="mvrp-header">
          <span className="mvrp-title">Motor Vehicle Record</span>
          <div className="mvrp-header__actions">
            {record && (
              <button className="mvrp-btn" onClick={() => handlePrint()}>Print</button>
            )}
            <button className="mvrp-close" onClick={onClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="mvrp-body">
          {loading && (
            <div className="mvrp-note">
              <span className="mvrp-spinner mvrp-spinner--dark mvrp-spinner--lg" />
              Loading the record…
            </div>
          )}
          {error && <div className="mvrp-note mvrp-note--err">{error}</div>}

          {preview?.documentUrl && (
            <iframe className="mvrp-doc" src={preview.documentUrl} title="MVR document" />
          )}

          {record && (
            <div ref={printRef} className="mvrp-record">
              <div className="mvrp-section">
                <div className="mvrp-section__title">Personal Information</div>
                <div className="mvrp-kv">
                  <div className="mvrp-kv__k">Name</div>
                  <div className="mvrp-kv__v">
                    {val([person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ') || driverName)}
                  </div>
                  <div className="mvrp-kv__k">Sex</div>
                  <div className="mvrp-kv__v">{val(person.gender)}</div>

                  <div className="mvrp-kv__k">Address</div>
                  <div className="mvrp-kv__v">{val(addressLine)}</div>
                  <div className="mvrp-kv__k">Eyes</div>
                  <div className="mvrp-kv__v">{val(person.eyeColor)}</div>

                  <div className="mvrp-kv__k">Age</div>
                  <div className="mvrp-kv__v">{val(person.age)}</div>
                  <div className="mvrp-kv__k">Hair</div>
                  <div className="mvrp-kv__v">{val(person.hairColor)}</div>

                  <div className="mvrp-kv__k">Weight</div>
                  <div className="mvrp-kv__v">{val(person.weight)}</div>
                  <div className="mvrp-kv__k">Date of Birth</div>
                  <div className="mvrp-kv__v">{val(person.birthDate)}</div>

                  <div className="mvrp-kv__k">License Number</div>
                  <div className="mvrp-kv__v">{val(person.licenseNumber)}</div>
                  <div className="mvrp-kv__k">License State</div>
                  <div className="mvrp-kv__v">{val(person.licenseState)}</div>
                </div>
              </div>

              <div className="mvrp-section">
                <div className="mvrp-section__title">Violations</div>
                <div className="mvrp-tscroll">
                  <table className="mvrp-table">
                    <thead>
                      <tr>
                        <th>Date</th><th>Conviction Date</th><th>State</th><th>Code</th>
                        <th>Description</th><th>Location</th><th>Speed</th><th>ACD</th><th>AVD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {record.violations.length === 0 ? (
                        <tr><td colSpan={9} className="mvrp-empty">No Violations</td></tr>
                      ) : record.violations.map((v, i) => (
                        <tr key={i}>
                          <td>{val(v.date)}</td><td>{val(v.convictionDate)}</td><td>{val(v.state)}</td>
                          <td>{val(v.code)}</td><td>{val(v.description)}</td><td>{val(v.location)}</td>
                          <td>{val(v.speed)}</td><td>{val(v.acd)}</td><td>{val(v.avd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mvrp-section">
                <div className="mvrp-section__title">Accidents</div>
                <div className="mvrp-tscroll">
                  <table className="mvrp-table">
                    <thead>
                      <tr><th>Date</th><th>Code</th><th>Description</th><th>Location</th><th>ACD</th><th>AVD</th></tr>
                    </thead>
                    <tbody>
                      {record.accidents.length === 0 ? (
                        <tr><td colSpan={6} className="mvrp-empty">No Accidents</td></tr>
                      ) : record.accidents.map((a, i) => (
                        <tr key={i}>
                          <td>{val(a.date)}</td><td>{val(a.code)}</td><td>{val(a.description)}</td>
                          <td>{val(a.location)}</td><td>{val(a.acd)}</td><td>{val(a.avd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mvrp-section">
                <div className="mvrp-section__title">License Information</div>
                <div className="mvrp-tscroll">
                  <table className="mvrp-table">
                    <thead>
                      <tr>
                        <th>Issue Date</th><th>Expire Date</th><th>Class</th><th>Class Description</th>
                        <th>Status</th><th>Restriction</th><th>CDL Type</th>
                        <th>CDL Expiration Date</th><th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {record.licences.length === 0 ? (
                        <tr><td colSpan={9} className="mvrp-empty">No License Information</td></tr>
                      ) : record.licences.map((l, i) => (
                        <tr key={i}>
                          <td>{val(l.issueDate)}</td><td>{val(l.expirationDate)}</td><td>{val(l.class)}</td>
                          <td>{val(l.classDescription)}</td><td>{val(l.status)}</td>
                          <td>{val(l.restrictions)}</td><td>{val(l.cdlMedicalType)}</td>
                          <td>{val(l.cdlMedicalExpirationDate)}</td><td>{val(l.type)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MvrPreviewModal;
